<div class="mc" [formGroup]="messageContentForm">
  <!-- ── Sender and subject ── -->
  <section class="bk-panel mc__panel" aria-labelledby="mc-details-title">
    <h2 class="bk-eyebrow mc__eyebrow" id="mc-details-title">Sender and subject</h2>
    <div class="mc__row">
      <input-field
        label="Subject"
        hint="Names the message in your lists. Recipients do not see it."
        [errorMessage]="
          submitted && messageContentForm.controls.messageSubject.invalid ? 'Give the message a subject.' : ''
        "
      >
        <input
          formControlName="messageSubject"
          placeholder="For example, October offer"
          class="input-style"
          autocomplete="off"
        />
      </input-field>

      <input-field
        label="Sender ID"
        hint="The name the message comes from on the phone."
        [errorMessage]="submitted && messageContentForm.controls.senderId.invalid ? 'Choose a sender ID.' : ''"
      >
        <bk-select
          formControlName="senderId"
          [options]="senderIDs"
          valueKey="senderId"
          variant="sender"
          noun="sender ID"
          placeholder="Choose a sender ID"
          emptyText="No sender IDs available"
          [loading]="senderLoading"
        />
      </input-field>
    </div>
  </section>

  <!-- ── Recipients, projected by the page ── -->
  <ng-content select="[recipients]"></ng-content>

  <!-- ── Message ── -->
  <section class="bk-panel mc__panel" aria-labelledby="mc-message-title">
    <div class="mc__head">
      <h2 class="bk-eyebrow mc__eyebrow" id="mc-message-title">Message</h2>
      <button
        type="button"
        class="bk-btn bk-btn--ghost mc__tpl-toggle"
        aria-controls="mc-templates"
        [attr.aria-expanded]="showTemplates"
        (click)="toggleTemplates()"
      >
        <span class="bk-sym" aria-hidden="true">{{ showTemplates ? "close" : "article" }}</span>
        {{ showTemplates ? "Close templates" : "Use a template" }}
      </button>
    </div>

    <!-- Template browser: searched on the server, each with its body and part count -->
    @if (showTemplates) {
      <div class="mc__tpl" id="mc-templates">
        <app-search-input
          class="mc__tpl-search"
          placeholder="Search templates by name"
          [debounce]="300"
          (valueChange)="onTemplateSearch($event)"
        />

        @if (pendingTemplate) {
          <div class="mc__confirm" role="alert">
            <p class="mc__confirm-text">
              Replace what you wrote with “{{ pendingTemplate.name || "this template" }}”?
            </p>
            <div class="mc__confirm-actions">
              <button type="button" class="bk-btn bk-btn--ghost" (click)="keepCurrentText()">Keep my text</button>
              <button type="button" class="bk-btn bk-btn--primary" (click)="useTemplate(pendingTemplate)">
                Replace
              </button>
            </div>
          </div>
        }

        <div class="mc__tpl-list" [attr.aria-busy]="templatesLoading">
          @if (templatesError) {
            <div class="mc__tpl-state">
              <p>{{ templatesError }}</p>
              <button type="button" class="bk-btn bk-btn--ghost" (click)="loadTemplates()">Try again</button>
            </div>
          } @else if (templatesLoading && !templates.length) {
            @for (row of [1, 2, 3]; track row) {
              <span class="bk-skel mc__tpl-skel" aria-hidden="true"></span>
            }
            <span class="bk-sr-only">Loading templates</span>
          } @else if (!templates.length) {
            <div class="mc__tpl-state">
              <p>
                @if (templateTerm.trim()) {
                  No templates match “{{ templateTerm.trim() }}”. Try a shorter name.
                } @else {
                  No templates yet. Save one from the Templates tab to reuse it here.
                }
              </p>
            </div>
          } @else {
            <ul class="mc__tpl-items" [class.is-loading]="templatesLoading">
              @for (template of templates; track $index) {
                <li>
                  <button type="button" class="mc__tpl-item" (click)="chooseTemplate(template)">
                    <span class="bk-msg mc__tpl-text">
                      <span class="bk-msg__subject">{{ template.name || "Untitled template" }}</span>
                      <span class="bk-msg__preview">
                        @if (template.content) {
                          <bk-sms-text [text]="template.content" [inline]="true" />
                        } @else {
                          No text
                        }
                      </span>
                    </span>
                    <span class="mc__tpl-parts">{{ templateParts(template.content) }}</span>
                  </button>
                </li>
              }
            </ul>

            @if (templateTotalPages > 1) {
              <nav class="mc__tpl-pager" aria-label="Template pages">
                <span>Page {{ templatePage + 1 }} of {{ templateTotalPages }}</span>
                <span class="mc__tpl-pager-btns">
                  <button
                    type="button"
                    class="bk-icon-btn"
                    aria-label="Previous page"
                    matTooltip="Previous page"
                    [disabled]="templatePage === 0 || templatesLoading"
                    (click)="goToTemplatePage(templatePage - 1)"
                  >
                    <span class="bk-sym" aria-hidden="true">chevron_left</span>
                  </button>
                  <button
                    type="button"
                    class="bk-icon-btn"
                    aria-label="Next page"
                    matTooltip="Next page"
                    [disabled]="templatePage >= templateTotalPages - 1 || templatesLoading"
                    (click)="goToTemplatePage(templatePage + 1)"
                  >
                    <span class="bk-sym" aria-hidden="true">chevron_right</span>
                  </button>
                </span>
              </nav>
            }
          }
        </div>
      </div>
    }

    @if (messageContentForm.value.template && !showTemplates) {
      <p class="mc__using">
        <span class="bk-sym" aria-hidden="true">article</span>
        <span>
          Started from {{ templateName ? "“" + templateName + "”" : "a template" }}. Changes here do not change the
          template.
        </span>
        <button type="button" class="mc__link" (click)="clearTemplate()">Unlink</button>
      </p>
    }

    <input-field
      label="Message"
      [errorMessage]="
        submitted && messageContentForm.controls.messageBody.invalid ? 'Write the message you want to send.' : ''
      "
    >
      <div class="mc__body">
        <textarea
          name="body"
          formControlName="messageBody"
          id="body"
          rows="6"
          class="input-style mc__textarea"
          placeholder="Write the SMS your recipients will get."
        ></textarea>
      </div>
    </input-field>

    <!-- Fields a template brought in. Inserting them is a Custom SMS feature, so there is no field button here. -->
    <message-attribute-chips
      [message]="messageContentForm.controls.messageBody.value"
      [costHint]="false"
      (remove)="removeAttribute($event)"
    ></message-attribute-chips>

    <!-- Live cost ruler -->
    <compose-sms-meter class="mc__meter" [text]="messageContentForm.controls.messageBody.value" />
  </section>

  <!-- ── Delivery ── -->
  <section class="bk-panel mc__panel" aria-labelledby="mc-delivery-title">
    <h2 class="bk-eyebrow mc__eyebrow" id="mc-delivery-title">Delivery</h2>

    <fieldset class="mc__when">
      <legend class="bk-sr-only">When to send</legend>
      <label class="mc__option" [class.is-on]="!isScheduled">
        <input type="radio" id="instant" formControlName="messageType" value="Instant" />
        <span class="bk-sym mc__option-icon" aria-hidden="true">send</span>
        <span class="mc__option-text">
          <span class="mc__option-name">Send now</span>
          <span class="mc__option-meta">Goes out as soon as you confirm</span>
        </span>
      </label>
      <label class="mc__option" [class.is-on]="isScheduled">
        <input type="radio" id="contactChoice2" formControlName="messageType" value="Scheduled" />
        <span class="bk-sym mc__option-icon" aria-hidden="true">schedule_send</span>
        <span class="mc__option-text">
          <span class="mc__option-name">Schedule</span>
          <span class="mc__option-meta">Pick a date and time</span>
        </span>
      </label>
    </fieldset>

    @if (isScheduled) {
      <div class="mc__schedule">
        <input-field
          class="mc__zone"
          label="Time zone"
          [errorMessage]="submitted && messageContentForm.controls.timeZone.invalid ? 'Choose a time zone.' : ''"
        >
          <select class="input-style" formControlName="timeZone">
            <option value="" disabled>Choose a time zone</option>
            <optgroup label="Suggested">
              @for (zone of zones.suggested; track zone) {
                <option [value]="zone">{{ zoneLabel(zone) }}</option>
              }
            </optgroup>
            <optgroup label="All time zones">
              @for (zone of zones.all; track zone) {
                <option [value]="zone">{{ zoneLabel(zone) }}</option>
              }
            </optgroup>
          </select>
        </input-field>

        <input-field
          label="Date"
          [errorMessage]="submitted && messageContentForm.controls.date.invalid ? 'Choose a date.' : ''"
        >
          <input type="date" class="input-style" formControlName="date" [min]="minDate" />
        </input-field>

        <input-field
          label="Time"
          [errorMessage]="submitted && messageContentForm.controls.time.invalid ? 'Choose a time.' : ''"
        >
          <input type="time" class="input-style" formControlName="time" />
        </input-field>
      </div>

      @if (schedule && schedule.at) {
        @if (scheduleInPast) {
          <p class="bk-field__error mc__when-line" role="alert" data-problem="true">
            <span class="bk-sym" aria-hidden="true">error</span>
            That time has passed. Pick a time in the future.
          </p>
        } @else {
          <p class="mc__when-line">
            <span class="bk-sym" aria-hidden="true">schedule</span>
            <span>
              Goes out <strong>{{ schedule.countdown }}</strong>, {{ schedule.when }}
              @if (schedule.local) {
                <span class="mc__quiet">({{ schedule.local }})</span>
              }
            </span>
          </p>
        }
      } @else {
        <p class="mc__when-line mc__quiet">Choose a date and time to see exactly when it goes out.</p>
      }
    }
  </section>
</div>
